import {pipe, useViewportSize} from '@yandex-delivery/utils'
import clsx from 'clsx'
import React, {useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'

import {ALPHA_STEP, setTopOffset, TRANSITION_DURATION} from './consts'
import {DrawerContextProvider} from './context'
import {classes} from './MobileDrawer.classes'
import {MobileDrawerOverlay} from './MobileDrawerOverlay'
import {Breakpoint} from './types'

export type MobileDrawerBaseProps = React.PropsWithChildren<{
  /** Открыт ли сейчас дровер */
  opened: boolean
  /** Колбек при закрытии дровера - пользователь смахнул вниз, нажал кнопку закрытия */
  onClose?: VoidFunction
  /** Можно ли закрыть дровер смахиванием вниз */
  persistent?: boolean
  /** Размеры, которые дровер может занимать в процентах от доступной высоты. 0 - полностью скрытый дровер, 1 - полностью развернутый. К указанным размерам добавляются 2 по умолчанию - 0 (если `persistent` не `true`) и автоматический, подразумевающий всю доступную высоту.
   * Например, `[0.33, 0.67]`
   **/
  breakpoints?: number[]
  /** Если `true`, оверлей не рисуется и за пределами дровера можно взаимодействовать со страницей. */
  disableOverlay?: boolean
  /** Класс, передающийся в тело дровера */
  className?: string
  /** Размер в пикселях, на сколько нужно отступить от верха страницы при максимальном размере. Например, чтобы оставить место для шапки. */
  topOffset?: number
  /** Вызывается когда дровер разворачивается на полную высоту */
  onMaximize?: VoidFunction
  /** Вызывается когда дровер двигают. `size` подчиняется тем же правилам, что и значения `breakpoints` */
  onMove?: (size: number, isFullSize: boolean) => void
  /** Контент, который не находится внутри дровера и не должно ограничиваться его скроллом. Например - кнопка закрытия. */
  outsideContent?: React.ReactNode
  /** Число от 0 до 1. Если размер дровера больше этого числа - оверлей активен. */
  overlayBreakpoint?: number
  /** Отключает мемоизацию вычисления высоты тела дровера(например если высота контента динамическая)
   * (Ухудшает производительность)
   */
  withoutMemorize?: boolean
  classNameOverlay?: string
  /**
   * Контейнер в котором отрисуется дровер
   */
  container?: React.MutableRefObject<HTMLElement>
  /**
   * Если false то тач-ивенты не пропагируются на случай если под дровером дровер
   * @default false
   */
  propagateTouchStartEvent?: boolean
  /**
   * Если true, то прозрачность оверлея будет меняться в зависимости от размера дровера
   * @default true
   */
  isOverlayOpacityChangeEnabled?: boolean
}>

const memoAutoValue = (() => {
  const memo: {el?: HTMLElement; value?: number} = {}

  return (ref: HTMLElement, calc: (el: HTMLElement) => number): number => {
    if (ref !== memo.el || memo.value === undefined) {
      memo.el = ref
      memo.value = calc(ref)
    }
    return memo.value
  }
})()

/**
 * Откладывает обновление до истечения `delay` миллисекунд
 * с момента последнего изменения `state`.
 *
 * Использует `useEffect` для отслеживания изменений `state`.
 */
const useDebouncedState = <T extends unknown>(state: T, delay: number): T => {
  const [debouncedState, setDebouncedState] = useState(state)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedState(state)
    }, delay)

    return () => {
      clearTimeout(timeout)
    }
  }, [state, delay])

  return debouncedState
}

export const MobileDrawerBase = ({
  opened: externalOpened,
  breakpoints: breakpointsProp = [],
  onClose: onCloseProp,
  disableOverlay = false,
  className,
  persistent,
  topOffset = 0,
  onMaximize,
  onMove,
  outsideContent,
  overlayBreakpoint = 0.4,
  children,
  withoutMemorize,
  classNameOverlay,
  container,
  propagateTouchStartEvent = false,
  isOverlayOpacityChangeEnabled = true,
}: MobileDrawerBaseProps) => {
  // Обходное решение проблемы https://github.com/adobe/react-spectrum/issues/1513
  // Предотвращает срабатывание кликов по элементам под оверлеем
  // за счет дебаунса состояния открытия
  const opened = useDebouncedState(externalOpened, 0)

  const overlayRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>()
  const bodyRef = useRef<HTMLDivElement>(null)
  const currentSizeRef = useRef<Breakpoint>()
  const {height: viewportHeight} = useViewportSize()
  // Трекаем свайпает ли юзер прям щас, если да - отключаем транзишены
  const [isSwiping, setIsSwiping] = useState(false)
  const [isFullSize, setIsFullSize] = useState(breakpointsProp.length === 0)
  const [isOverlayDisabled, setIsOverlayDisabled] = useState(disableOverlay)

  // Рефы - отслеживаем состояние тача во время свайпа, чтобы на его основе определять поведение завершения жеста
  // Нужно чтобы отслеживать направление последнего свайпа
  const _swipeDiff = useRef(0)
  // Нужно чтобы трекать на сколько пикселей юзер просвайпал в ивенте
  const _swipeStartY = useRef(0)
  // Нужно чтобы выставить транзишен, подходящий по скорости жесту пользователя
  const _swipeSpeed = useRef<number[]>([])
  // Нужно для определения скорости свайпа
  const _lastSwipeTimestamp = useRef<number>()

  // Т.к. пересчитывать высоту элемента каждый раз долго (~15ms на моем маке), кэшируем значение и пересчитываем только когда референс к элементу меняется.
  function autoValue() {
    if (!bodyRef.current) {
      return 1
    }

    if (withoutMemorize) {
      return Math.min(1, bodyRef.current.clientHeight / viewportHeight)
    }

    return memoAutoValue(bodyRef.current, el => Math.min(1, el.clientHeight / viewportHeight))
  }

  // Утилиты для преобразования брейкпоинтов в числа и обратно. Нужно для адекватной поддержки автовысоты
  const fromBreakpoint = (bp: Breakpoint) => (bp === 'auto' ? autoValue() : Math.min(bp, autoValue()))
  const toBreakpoint = (value: number) => (value >= autoValue() ? 'auto' : Math.max(value, 0))
  //

  const calcAlpha = (size: Breakpoint) =>
    Math.max(0, Math.round((fromBreakpoint(size) / autoValue() - overlayBreakpoint) / ALPHA_STEP) * ALPHA_STEP)
  const defaultBreakpoint = breakpointsProp[0] || 'auto'

  const isInsideContainer = Boolean(container?.current)

  const updateSize = (setter: (prevSize: Breakpoint) => Breakpoint = b => b) => {
    if (!contentRef.current) {
      return
    }
    const el = contentRef.current
    // Когда свайпаем, 0 - приемлемый брейкпоинт, в остальных случаях - нет
    const prevSize = isSwiping
      ? currentSizeRef.current ?? defaultBreakpoint
      : currentSizeRef.current || defaultBreakpoint
    const newSize = setter(prevSize)
    currentSizeRef.current = newSize

    if (newSize === 'auto') {
      onMaximize?.()
    }

    onMove?.(fromBreakpoint(newSize), newSize === 'auto')

    window.requestAnimationFrame(() => {
      el.style.setProperty(
        'transform',
        isInsideContainer
          ? `translateY(${Math.max(0, topOffset)}px)`
          : `translateY(${Math.max(0, viewportHeight * (1 - fromBreakpoint(newSize)) - topOffset)}px)`,
      )
      
      if (isOverlayOpacityChangeEnabled) {
        overlayRef.current?.style.setProperty('opacity', calcAlpha(newSize).toString())
      }
    })
    return newSize
  }

  const breakpoints: Breakpoint[] = [...(persistent ? [] : [0]), 'auto', ...breakpointsProp]

  const onClose = () => {
    if (!persistent) {
      updateSize(() => 0)
    }
    contentRef.current?.style.removeProperty('transition-duration')
    contentRef.current?.style.removeProperty('transition-timing-function')
    onCloseProp?.()
  }

  const onTouchStart: React.TouchEventHandler<HTMLDivElement> = e => {
    // Если юзер пытается скроллить, не мешаем
    if (
      !e.touches[0] ||
      (e.target instanceof HTMLElement && e.target.scrollTop > 0) ||
      !bodyRef.current ||
      bodyRef.current.scrollTop > 0
    ) {
      return
    }

    // На случай если под дровером будет еще дровер
    if (!propagateTouchStartEvent) {
      e.stopPropagation()
    }
    setIsSwiping(true)
    contentRef.current?.style.setProperty('transition', 'unset')
    _swipeStartY.current = e.touches[0].clientY
  }

  const onTouchMove: React.TouchEventHandler<HTMLDivElement> = e => {
    if (!e.touches[0] || !isSwiping || !bodyRef.current) {
      return
    }
    if ((e.target instanceof HTMLElement && e.target.scrollTop > 0) || bodyRef.current.scrollTop > 0) {
      setIsSwiping(false)
      return
    }
    const diff = e.touches[0].clientY - _swipeStartY.current
    _swipeDiff.current = diff
    _swipeStartY.current = e.touches[0].clientY

    // Сохраняем последние 5 кадров для амортизации вычисления скорости
    _swipeSpeed.current = [
      ..._swipeSpeed.current.slice(-4),
      ...(_lastSwipeTimestamp.current ? [Math.abs(diff) / (performance.now() - _lastSwipeTimestamp.current)] : []),
    ]
    _lastSwipeTimestamp.current = performance.now()

    updateSize(size => {
      setIsFullSize(size === 'auto')
      return pipe(size, fromBreakpoint, size => size - diff / viewportHeight, toBreakpoint)
    })
  }

  const onTouchEnd: React.TouchEventHandler<HTMLDivElement> = () => {
    setIsSwiping(false)
    if (!contentRef.current) {
      return
    }
    updateSize(prevSize => {
      contentRef.current?.style.removeProperty('transition')

      // Если пользовал не двигал дровер, то размер не меняем
      if (_swipeDiff.current === 0) {
        return prevSize
      }

      const swipedUpwards = _swipeDiff.current <= 0
      const absoluteBreakpoints = breakpoints.map(bp => fromBreakpoint(bp))

      // Если последнее движение в свайпе - вверх, то движемся к ближайшему брейкпоинту больше текущего размера. Если вниз - к ближайшему меньше текущего размера.
      const validBreakpoints = absoluteBreakpoints.filter(bp => (swipedUpwards ? bp > prevSize : bp < prevSize))
      // Если нет подходящих брейкпоинтов, дефолтимся на максимальный и минимальный из всего набора
      if (validBreakpoints.length === 0) {
        validBreakpoints.push(swipedUpwards ? Math.max(...absoluteBreakpoints) : Math.min(...absoluteBreakpoints))
      }
      const endBreakpoint = toBreakpoint(swipedUpwards ? Math.min(...validBreakpoints) : Math.max(...validBreakpoints))
      setIsFullSize(endBreakpoint === 'auto')

      // Если пользователь смахнул дровер вниз, закрываем его
      if (endBreakpoint === 0) {
        onClose()
      }

      // Подстраиваем тайминг транзишена чтобы он завершился с той же скоростью с которой пользователь тянул шторку
      if (_swipeDiff.current && _lastSwipeTimestamp.current) {
        const swipeVelocity = _swipeSpeed.current.reduce((a, b) => a + b, 1) / _swipeSpeed.current.length
        const transitionDistance = (fromBreakpoint(prevSize) - fromBreakpoint(endBreakpoint)) * viewportHeight
        // V = S / t; t = S / V
        // Выставляем минимальный уровень скорости, чтобы шторка не ползла совсем медленно
        contentRef.current?.style.setProperty(
          'transition-duration',
          `${Math.min(TRANSITION_DURATION, Math.abs(transitionDistance / swipeVelocity))}ms`,
        )
        contentRef.current?.style.setProperty('transition-timing-function', 'linear')
        _swipeSpeed.current = []
        _lastSwipeTimestamp.current = undefined
      }
      return opened ? endBreakpoint : 0
    })
  }

  return createPortal(
    <MobileDrawerOverlay
      bgRef={overlayRef}
      closeDrawer={onClose}
      isDisabled={disableOverlay || (!isSwiping && isOverlayDisabled)}
      isSwiping={isSwiping}
      opened={opened}
      className={classNameOverlay}
      isInsideContainer={isInsideContainer}
      {...{onTouchStart, onTouchMove, onTouchEnd}}
    >
      <div
        ref={el => {
          if (!el) {
            return
          }
          contentRef.current = el
          if (opened) {
            const size = updateSize()
            size && setIsOverlayDisabled(calcAlpha(size) === 0)
          }
        }}
        className={clsx(classes.drawerContainer, isInsideContainer && classes.insideContainer.drawerContainer)}
        style={{
          ...setTopOffset(`${isInsideContainer ? 0 : topOffset}px`),
          blockSize: isInsideContainer ? `calc(100% - ${topOffset}px)` : 'unset',
        }}
      >
        <DrawerContextProvider value={{onClose, isFullSize, opened}}>
          {outsideContent}
          <div
            ref={bodyRef}
            className={clsx(
              classes.drawerBody,
              isInsideContainer && classes.insideContainer.drawerBody,
              className,
              isFullSize && classes.scrollable,
            )}
          >
            {children}
          </div>
        </DrawerContextProvider>
      </div>
    </MobileDrawerOverlay>,
    container?.current || document.body,
  )
}
